import { ScholarshipManagementDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as ScholarshipManagementActions from './scholarship-management.actions';

export const SCHOLARSHIP_MANAGEMENT_FEATURE_KEY = 'scholarshipmanagement';

export interface ScholarshipManagementState
  extends EntityState<ScholarshipManagementDto> {
  selectedScholarshipManagementId?: string | number; // which ScholarshipManagement record has been selected
  loaded: boolean; // has the ScholarshipManagement list been loaded
  error?: string | null; // last none error (if any)
}

export interface ScholarshipManagementPartialState {
  readonly [SCHOLARSHIP_MANAGEMENT_FEATURE_KEY]: ScholarshipManagementState;
}

export const scholarshipManagementAdapter: EntityAdapter<ScholarshipManagementDto> = createEntityAdapter<
  ScholarshipManagementDto
>({
  selectId: (scholarshipManagement) => scholarshipManagement.id,
  sortComparer: false,
});

export const initialState: ScholarshipManagementState = scholarshipManagementAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedScholarshipManagementId: null,
  }
);

const scholarshipManagementReducer = createReducer(
  initialState,
  on(ScholarshipManagementActions.loadScholarshipManagement, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ScholarshipManagementActions.loadScholarshipManagementSuccess,
    (state, { scholarshipManagement }) =>
      scholarshipManagementAdapter.addAll(scholarshipManagement, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ScholarshipManagementActions.loadScholarshipManagementFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(ScholarshipManagementActions.addScholarshipManagement, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ScholarshipManagementActions.addScholarshipManagementSuccess,
    (state, { newScholarshipManagement }) =>
      scholarshipManagementAdapter.addOne(newScholarshipManagement, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ScholarshipManagementActions.addScholarshipManagementFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(ScholarshipManagementActions.uploadExcelStudentRecord, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ScholarshipManagementActions.uploadExcelStudentRecordSuccess,
    (state, { uploadExcelRecord }) =>
      scholarshipManagementAdapter.addMany(uploadExcelRecord, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ScholarshipManagementActions.uploadExcelStudentRecordFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(ScholarshipManagementActions.updateScholarshipManagement, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ScholarshipManagementActions.updateScholarshipManagementSuccess,
    (state, { updateScholarshipManagement }) =>
      scholarshipManagementAdapter.upsertOne(updateScholarshipManagement, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ScholarshipManagementActions.updateScholarshipManagementFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  // search student

  on(
    ScholarshipManagementActions.searchScholarshipManagementRecord,
    (state, { query }) => {
      return {
        ...state,
        selectedScholarshipManagementId: query,
        loaded: false,
        error: null,
      };
    }
  ),
  on(ScholarshipManagementActions.removeScholarshipManagement, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ScholarshipManagementActions.removeScholarshipManagementSuccess,
    (state, { ids }) =>
      scholarshipManagementAdapter.removeOne(ids, { ...state, loaded: false })
  ),
  on(
    ScholarshipManagementActions.removeScholarshipManagementFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: ScholarshipManagementState | undefined,
  action: Action
) {
  return scholarshipManagementReducer(state, action);
}
